/*
 * Row rules for the report. Money is read from the data (money.booking.*), never derived here.
 * money.booking is shared by every row of a booking, so anything that adds it up does so once
 * per bookingId (summarize).
 */

// The API's limit for a status note (POST registrationReport/statusNote).
export const STATUS_NOTE_MAX = 40;

export const PAYMENT_STATE_LABELS = {
  pending: 'Payment pending',
  overdue: 'Overdue',
  balance_due: 'Balance due',
  refunded: 'Refunded',
  not_charged: 'Not charged',
  paid: 'Paid',
};

// Attending = not cancelled: active, payment pending, or refunded without cancelling (status 3).
export function isActive(r) {
  return r.statusKey === 'active' || r.statusKey === 'pending' || r.statusKey === 'refunded';
}

// Cancelled, with or without a refund (status 2 / 4, or isCancelled).
export function isCancelledRow(r) {
  return r.statusKey === 'cancelled' || r.statusKey === 'cancelled_refunded';
}

// The booking's balance, shared by every row of the booking.
export function balanceDue(r) {
  return Number(r.money?.booking?.balanceDue) || 0;
}

export function getPaymentState(r) {
  const state = r.money?.booking?.paymentState;
  return PAYMENT_STATE_LABELS[state] || state || '';
}

export function documentsComplete(r) {
  return (r.documents?.missingCount || 0) === 0;
}

/*
 * The four document rules (the booking page's Documents card), labelled as the booking page labels them.
 * A form that isn't asked for is null: Key Deposit for commuters, and any form the program's
 * switches leave out (concussion waiver, medical clearance). The booking page leaves those out;
 * here they show as not required. Counts use the data's missingCount.
 */
export function documentChecklist(r) {
  const d = r.documents || {};
  const item = (label, value, note = '') => ({ label, note, state: value === null || value === undefined ? 'not_required' : value ? 'received' : 'missing' });
  const items = [
    item('Key Deposit', d.keyDeposit, r.stay === 'Commuter' ? 'commuter' : ''),
    item('Concussion Waiver', d.concussion),
    item('Medical Clearance Form & Physical', d.medicalDocument),
    item('Waiver Disclaimer', d.disclaimer),
  ];
  const required = items.filter(i => i.state !== 'not_required').length;
  return { items, required, received: Math.max(0, required - (d.missingCount || 0)) };
}

// Only when a medical document is on file and not approved yet. A missing document is already
// counted by documents.missingCount.
export function medicalNeedsReview(r) {
  return Boolean(r.documents?.medicalDocument) && !r.documents?.medicalApproved;
}

// Medical review for the table, filter, CSV and drawer. medicalDocument is null when the program
// doesn't ask for the medical clearance form.
export function medicalReviewLabel(r) {
  const doc = r.documents?.medicalDocument;
  if (doc === null || doc === undefined) return 'Not required';
  if (!doc) return 'No document';
  return medicalNeedsReview(r) ? 'Needs review' : 'Approved';
}

// The forms store "None" as an answer; it counts as nothing to report.
const reported = value => Boolean(value) && !/^none$/i.test(String(value).trim());

export function hasMedicalInfo(r) {
  return reported(r.participant.medical) || reported(r.participant.allergy) || Number(r.participant.allergyDeadly) === 1;
}

export function needsAttention(r) {
  return isActive(r) && (balanceDue(r) > 0 || Boolean(r.money?.booking?.overdue) || !documentsComplete(r) || medicalNeedsReview(r));
}

export const TAB_KEYS = ['all', 'attention', 'medical', 'cancelled'];

export function matchesTab(r, tab) {
  if (tab === 'attention') return needsAttention(r);
  if (tab === 'medical') return hasMedicalInfo(r);
  if (tab === 'cancelled') return isCancelledRow(r);
  return true;
}

export function matchesSearch(r, query) {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const digits = q.replace(/\D/g, '');
  const haystack = [
    r.bookingNumber,
    r.participant.firstName,
    r.participant.lastName,
    `${r.participant.firstName} ${r.participant.lastName}`,
    r.primaryGuardian.name,
    r.primaryGuardian.email,
    r.secondaryGuardian?.name ?? '',
    r.secondaryGuardian?.email ?? '',
    r.statusNote,
    r.program.programName,
    r.address.street,
    r.address.city,
    r.address.zip,
  ]
    .join(' ')
    .toLowerCase();
  if (haystack.includes(q)) return true;
  if (digits.length >= 3) {
    const phones = [r.primaryGuardian.phone, r.secondaryGuardian?.phone ?? ''].join(' ').replace(/\D/g, '');
    return phones.includes(digits);
  }
  return false;
}

/*
 * Summary for the cards. Booking money (balance due, paid, coupon) is added once per bookingId, and
 * only for bookings with at least one active row in `rows`; a booking whose rows here are all
 * cancelled adds nothing. Collected % = paid / (paid + balance due): booking `total` is
 * what was charged at checkout, not the price, on payment-plan and pay-later bookings.
 */
export function summarize(rows) {
  const active = rows.filter(isActive);
  const bookings = new Map();
  active.forEach(r => {
    if (!bookings.has(r.bookingId)) bookings.set(r.bookingId, r.money?.booking || {});
  });

  let amountDue = 0;
  let collected = 0;
  let couponTotal = 0;
  let couponCount = 0;
  bookings.forEach(b => {
    amountDue += Number(b.balanceDue) || 0;
    collected += Number(b.paid) || 0;
    if (Number(b.couponValue) > 0) {
      couponTotal += Number(b.couponValue);
      couponCount += 1;
    }
  });

  // Float sums of money values (cents), rounded back to cents.
  const cents = value => Math.round(value * 100) / 100;
  amountDue = cents(amountDue);
  collected = cents(collected);
  couponTotal = cents(couponTotal);

  const complete = active.filter(documentsComplete).length;
  const base = collected + amountDue;
  return {
    count: rows.length,
    bookingCount: bookings.size,
    amountDue,
    collected,
    collectedPct: base > 0 ? Math.round((collected / base) * 100) : 0,
    registered: active.length,
    cancelled: rows.filter(isCancelledRow).length,
    overnight: active.filter(r => r.stay === 'Overnight').length,
    commuter: active.filter(r => r.stay === 'Commuter').length,
    documentsComplete: complete,
    missingDocuments: active.length - complete,
    couponTotal,
    couponCount,
  };
}
